// Regras da revisão do acervo LEGADO: fotos antigas migradas para o sistema
// passam por um crivo foto a foto (aprovar/reprovar). O veredito da máquina
// não é digitado — ele é derivado do que foi marcado nas fotos.

import type { UploadedFoto, UploadedMachineFolder } from '@/services/api';
import type { Variaveis } from '@/lib/i18n';

/**
 * Motivos prontos de reprovação (o revisor ainda pode escrever o dele).
 * `valor` é o que vai gravado na revisão da foto e volta do banco depois, então
 * fica sempre em português; só o rótulo do botão é traduzido.
 */
export const MOTIVOS_REPROVACAO: Array<{ valor: string; chave: string }> = [
  { valor: 'Foto tremida ou escura', chave: 'imagens.revisaoLegado.motivos.tremida' },
  { valor: 'Enquadramento insuficiente', chave: 'imagens.revisaoLegado.motivos.enquadramento' },
  { valor: 'Falta a placa de identificação', chave: 'imagens.revisaoLegado.motivos.placa' },
  { valor: 'Falta o painel elétrico', chave: 'imagens.revisaoLegado.motivos.painel' },
  { valor: 'Não é da máquina', chave: 'imagens.revisaoLegado.motivos.outraMaquina' },
  { valor: 'Duplicada', chave: 'imagens.revisaoLegado.motivos.duplicada' },
];

export interface ResumoRevisao {
  total: number;
  aprovadas: number;
  reprovadas: number;
  pendentes: number;
  /** 0–100: quanto do acervo da pasta já recebeu veredito. */
  percentual: number;
}

export function resumoRevisao(fotos: UploadedFoto[]): ResumoRevisao {
  const total = fotos.length;
  const aprovadas = fotos.filter((f) => f.revisao?.status === 'aprovada').length;
  const reprovadas = fotos.filter((f) => f.revisao?.status === 'reprovada').length;
  const revisadas = aprovadas + reprovadas;
  return {
    total,
    aprovadas,
    reprovadas,
    pendentes: total - revisadas,
    percentual: total > 0 ? Math.round((revisadas / total) * 100) : 0,
  };
}

export type StatusRevisaoPasta = 'sem_fotos' | 'nao_revisada' | 'em_revisao' | 'precisa_mais_imagens' | 'aprovada';

export interface VereditoPasta {
  value: StatusRevisaoPasta;
  /** Chave i18n do selo — quem renderiza resolve com `t(labelChave, labelVars)`. */
  labelChave: string;
  labelVars?: Variaveis;
  badgeClass: string;
  dotClass: string;
  /** Chave i18n da explicação curta usada em tooltip. */
  detalheChave: string;
  detalheVars?: Variaveis;
}

/**
 * Veredito da máquina derivado das fotos:
 * qualquer pendência → em revisão; alguma reprovada (e nada pendente) →
 * precisa de mais imagens; tudo aprovado → aprovada.
 */
export function vereditoPasta(resumo: ResumoRevisao): VereditoPasta {
  if (resumo.total === 0) {
    return {
      value: 'sem_fotos',
      labelChave: 'imagens.revisaoLegado.veredito.semFotos',
      badgeClass: 'bg-gray-200 text-gray-600 border border-gray-300',
      dotClass: 'bg-gray-400',
      detalheChave: 'imagens.revisaoLegado.veredito.semFotosDetalhe',
    };
  }
  if (resumo.pendentes === resumo.total) {
    return {
      value: 'nao_revisada',
      labelChave: 'imagens.revisaoLegado.veredito.naoRevisada',
      badgeClass: 'bg-sky-100 text-sky-700 border border-sky-300',
      dotClass: 'bg-sky-500',
      detalheChave: 'imagens.revisaoLegado.veredito.naoRevisadaDetalhe',
      detalheVars: { total: resumo.total },
    };
  }
  if (resumo.pendentes > 0) {
    return {
      value: 'em_revisao',
      labelChave: 'imagens.revisaoLegado.veredito.emRevisao',
      labelVars: { pendentes: resumo.pendentes },
      badgeClass: 'bg-amber-100 text-amber-700 border border-amber-300',
      dotClass: 'bg-amber-500',
      detalheChave: 'imagens.revisaoLegado.veredito.emRevisaoDetalhe',
      detalheVars: {
        aprovadas: resumo.aprovadas,
        reprovadas: resumo.reprovadas,
        pendentes: resumo.pendentes,
      },
    };
  }
  if (resumo.reprovadas > 0) {
    return {
      value: 'precisa_mais_imagens',
      labelChave: 'imagens.revisaoLegado.veredito.precisaMaisImagens',
      badgeClass: 'bg-rose-100 text-rose-700 border border-rose-300',
      dotClass: 'bg-rose-500',
      detalheChave: 'imagens.revisaoLegado.veredito.precisaMaisImagensDetalhe',
      detalheVars: { reprovadas: resumo.reprovadas, total: resumo.total },
    };
  }
  return {
    value: 'aprovada',
    labelChave: 'imagens.revisaoLegado.veredito.aprovada',
    badgeClass: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
    dotClass: 'bg-emerald-500',
    detalheChave: 'imagens.revisaoLegado.veredito.aprovadaDetalhe',
    detalheVars: { total: resumo.total },
  };
}

export const vereditoDaPasta = (folder: UploadedMachineFolder): VereditoPasta =>
  vereditoPasta(resumoRevisao(folder.fotos));

/** Pasta que ainda tem foto sem veredito (usado pelo filtro da aba Legado). */
export const temFotoPendente = (folder: UploadedMachineFolder): boolean =>
  folder.fotos.some((foto) => !foto.revisao);
